import type { ActivityHeatCell, ActivityLoginPoint, ActivityWindow } from '@bbbk/schema';
import { chartFormatDay } from '../bookings/chartParts';
import { SECTIONS, sectionKey } from './activityFormat';

// --- Shared y-scale for the count charts -------------------------------------
// Round the peak up to a multiple of 4 (min 4) so the four gridline ticks land
// on whole numbers. ONE place, so the section-bar and sign-ins-line y-axes can't
// drift — both call this.
export function countAxis(peak: number): { max: number; ticks: number[] } {
  const max = Math.max(4, Math.ceil(Math.max(0, peak) / 4) * 4);
  return { max, ticks: [0, 1, 2, 3, 4].map((i) => Math.round((max * i) / 4)) };
}

// Pure shaping for the three Account chart cards. Kept out of the components so
// the aggregation rules unit-test without a DOM (the heatmap/feed pattern).

// --- Card 1: page views by section (bar) -------------------------------------
export interface SectionTotal {
  key: string;
  label: string; // full name (tooltip)
  short: string; // compact x-axis label
  value: number;
}

// Sum the heatmap cells (page views per actor per section) down to one total per
// nav section, in declared SECTIONS order, zero-filling sections with no views.
// Finer-grained labels (the staff tabs) roll up via sectionKey; labels outside
// SECTIONS (e.g. 'account') are dropped — same rule the heatmap used, so the
// bar chart charts exactly what the grid did, collapsed across people.
export function sectionTotals(cells: ActivityHeatCell[]): SectionTotal[] {
  const sums = new Map<string, number>();
  for (const c of cells) {
    const key = sectionKey(c.section);
    sums.set(key, (sums.get(key) ?? 0) + c.count);
  }
  return SECTIONS.map((s) => ({ key: s.key, label: s.label, short: s.short, value: sums.get(s.key) ?? 0 }));
}

// --- Card 2: sign-ins over time (line) ---------------------------------------
export interface LoginPoint {
  day: string; // 'YYYY-MM-DD'
  count: number;
  label: string; // 'd Mon' axis label
}

const DAY_MS = 86_400_000;
// Local-day helpers — the buckets are Dublin-local (matching the endpoint), and
// the whole app runs one locale, so local date math is the source of truth.
function startOfDay(t: number): number {
  const d = new Date(t);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}
function isoDay(t: number): string {
  const d = new Date(t);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
function parseDay(s: string): number {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).getTime();
}

// Turn the sparse per-day sign-in counts into a CONTINUOUS axis (zero-filled) so
// the line never skips quiet days. The span follows the window: a fixed 7/30-day
// tail for week/month, or earliest-point→today for 'all'.
export function loginsSeries(points: ActivityLoginPoint[], window: ActivityWindow, now: number = Date.now()): LoginPoint[] {
  const byDay = new Map(points.map((p) => [p.day, p.count]));
  const today = startOfDay(now);
  let start: number;
  if (window === 'week') start = today - 6 * DAY_MS;
  else if (window === 'month') start = today - 29 * DAY_MS;
  else start = points.length ? parseDay(points[0]!.day) : today;
  const out: LoginPoint[] = [];
  for (let t = start; t <= today; t += DAY_MS) {
    const day = isoDay(t);
    out.push({ day, count: byDay.get(day) ?? 0, label: chartFormatDay(day) });
  }
  return out;
}

// Card 3 (Team by role) reuses roleCounts from ./filter against the allowlist the
// page already holds — no shaping helper needed here.
